import { isSafariWithAudioContextBug } from "./general";

export interface IPauseAudioInput {
	excludedIds?: string[];
	resetToStart?: boolean;
    stopBkgrAudio?: boolean;
}

export interface IPlayAudioInput {
    entityIds: string[];
    loop?: boolean;
}

interface ISetupAudioData {
    src: string,
    loop: boolean,
    id: string;
}

export type ISetupAudioInput = ISetupAudioData[];

type IPauseAudio = (a?: IPauseAudioInput) => void;
type ISetupAudio = (audioContext: AudioContext, audioStreamDestination: MediaStreamAudioDestinationNode, data: ISetupAudioInput) => void;
type IPlayAudio = (a: IPlayAudioInput & { experienceIsMuted: boolean;}) => void;

interface IAudioDict {
    [audioId: string]: HTMLAudioElement[]
}

/** A live audio element plus the graph node that has to be disconnected when it is discarded. */
interface IAudioEntry {
    el: HTMLAudioElement;
    sourceNode: MediaElementAudioSourceNode | null;
}

/**
 * Identity of one sound within a scene, for matching across content updates.
 *
 * Keyed on id *and* src because one entity id can own several sounds (one per action), so the id alone
 * does not identify an element. The occurrence counter disambiguates the same file used twice under the
 * same id. NUL is the separator so it cannot collide with anything inside a URL or an id.
 */
const _audioEntryKey = (id: string, src: string, occurrence: number): string => `${id} ${src} ${occurrence}`;

class SceneAudioManager {
    private _audioDict: IAudioDict = {};
    /** Live entries keyed by `_audioEntryKey`, so a content update can tell survivors from newcomers. */
    private _entryByKey = new Map<string, IAudioEntry>();
    public bkgrAudio: HTMLAudioElement;
    private _bkgrAudioHasFinished = false;

    constructor(bkgrAudio: HTMLAudioElement) {
        this.bkgrAudio = bkgrAudio;
        this.bkgrAudio.addEventListener('ended', () => this._bkgrAudioHasFinished = true);
    }

    /**
     * Reconcile the scene's audio elements against `sceneAudio`, reusing every element that survives.
     *
     * This used to construct a fresh `Audio` per sound on every call, with the caller throwing the whole
     * manager away and rebuilding it. That cut off playback on any audio edit, leaked a graph node per
     * discarded element, and — the reason it matters for live preview — re-primed each element outside a
     * user gesture. An element is blessed for playback by the `play()` below landing near the user's
     * launch tap, and it keeps that blessing across its lifetime; a replacement created mid-session has
     * no such guarantee. So sounds that did not change are now left completely untouched, and only
     * genuinely new ones are built.
     */
    setupAudio: ISetupAudio = (audioContext, audioStreamDestination, sceneAudio) => {
        const nextDict: IAudioDict = {};
        const nextEntries = new Map<string, IAudioEntry>();
        const occurrences = new Map<string, number>();

        for (let i = 0; i < sceneAudio.length; i++) {
            const {src, id: audioId, loop} = sceneAudio[i];
            if (!src || !audioId) continue;

            const base = `${audioId} ${src}`;
            const occurrence = occurrences.get(base) ?? 0;
            occurrences.set(base, occurrence + 1);
            const key = _audioEntryKey(audioId, src, occurrence);

            // Reused when the same sound is still present. `loop` is the only thing that can have changed
            // without changing the key, and it is a plain property — no rebuild needed for it.
            let entry = this._entryByKey.get(key);
            if (entry) {
                if (typeof loop !== 'undefined') entry.el.loop = loop;
            } else {
                entry = this._createAudioEntry(src, loop, audioContext, audioStreamDestination);
            }

            nextEntries.set(key, entry);
            if (!nextDict[audioId]) nextDict[audioId] = [];
            nextDict[audioId].push(entry.el);
        }

        // Anything no longer referenced is genuinely gone — release it rather than just dropping it.
        this._entryByKey.forEach((entry, key) => {
            if (!nextEntries.has(key)) this._disposeAudioEntry(entry);
        });

        this._entryByKey = nextEntries;
        this._audioDict = nextDict;
    }

    private _createAudioEntry = (
        src: string,
        loop: boolean | undefined,
        audioContext: AudioContext,
        audioStreamDestination: MediaStreamAudioDestinationNode
    ): IAudioEntry => {
        const audioEl = new Audio(src);
        audioEl.crossOrigin = "anonymous";
        if (typeof loop !== 'undefined') audioEl.loop = loop;

        // muting audio el and pausing after play start to ensure safari multi-audiotrack permission
        // is granted when playing multiple audio later
        audioEl.muted = true;
        let sourceNode: MediaElementAudioSourceNode | null = null;
        try {
            audioEl.play().then(() => {
                audioEl.pause();
            })
            if (!isSafariWithAudioContextBug()) {
                sourceNode = addMediaElementToAudioStreamDestination(audioContext, audioStreamDestination, audioEl);
            }
            // Pause and mute audio again in case adding node to audio context results in immediate audio triggering
            audioEl.muted = true;
            audioEl.pause();
            audioEl.currentTime = 0;
        } catch (error) {
            console.warn(error)
        }

        return { el: audioEl, sourceNode };
    }

    private _disposeAudioEntry = (entry: IAudioEntry): void => {
        try {
            entry.sourceNode?.disconnect();
            entry.el.pause();
            entry.el.removeAttribute('src');
            entry.el.load();
        } catch (error) {
            console.warn('Error disposing audio element: ', error);
        }
    }

    playAudio: IPlayAudio = ({entityIds, loop = false, experienceIsMuted = false}) => {
        let numberAudioPlaying = 0;
        for (let i = 0; i < entityIds.length; i++) {
            const audioId = entityIds[i];
            const audioElArray = this._audioDict[audioId];
            if (typeof audioElArray === 'undefined') continue;
            for (let j = 0; j < audioElArray.length; j++) {
                const audioEl = audioElArray[j];
                if (loop) audioEl.loop = true;
                audioEl.currentTime = 0;
                // unmuting audio after play start to ensure safari multi-audiotrack permission granted
                audioEl.play().then(() => audioEl.muted = experienceIsMuted)

                // audioEl.play();
                numberAudioPlaying++

                // `once`: this listener belongs to *this* play, and elements now survive across content
                // updates, so without it a sound replayed N times would carry N listeners, each firing on
                // every later `ended` and decrementing a counter it no longer owns.
                audioEl.addEventListener('ended', ()  => {
                    if (numberAudioPlaying === 1 && this.bkgrAudio.paused && !this._bkgrAudioHasFinished) {
                        this.bkgrAudio.play();
                    }
                    numberAudioPlaying--;
                }, { once: true });
            }

            
        }
    }

    pauseAudio: IPauseAudio = (args) => {
        if (args?.stopBkgrAudio) this.bkgrAudio.pause();
        const audioIds = Object.keys(this._audioDict);
        for (let i = 0; i < audioIds.length; i++) {
            const audioId = audioIds[i];
            const audioElArray = this._audioDict[audioId];
            if (typeof audioElArray === 'undefined') continue;
            for (let j = 0; j < audioElArray.length; j++) {
                const audioEl = audioElArray[j];
                if (Array.isArray(args?.excludedIds) && args?.excludedIds?.includes(audioId)) continue;
                audioEl.pause();
                if (args?.resetToStart) audioEl.currentTime = 0;
            }
        }
    }
    unmuteAllPlayingAudio = (): void => {
        for (const id in this._audioDict) {
            const audioElArray = this._audioDict[id];
            if (typeof audioElArray === 'undefined') continue;
            for (let i = 0; i < audioElArray.length; i++) {
                const audioEl = audioElArray[i];
                if (!audioEl.paused) audioEl.muted = false
            }
           
        }
    } 
    muteAllAudio = (): void => {
        for (const id in this._audioDict) {
            const audioElArray = this._audioDict[id];
            if (typeof audioElArray === 'undefined') continue;
            for (let i = 0; i < audioElArray.length; i++) {
                const audioEl = audioElArray[i];
                audioEl.muted = true;
            }
        }
    } 

    // getAllSceneAudioElements = (): HTMLAudioElement[] => {
    //     const audioElements = Object.values(this._audioDict).map(data => data[1]);
    //     audioElements.push(this.bkgrAudio);
    //     return audioElements;
    // }

    playBackgroundAudio = (muted: boolean): void => {
        if(this._bkgrAudioHasFinished || !this.bkgrAudio.paused) return;
        this.bkgrAudio.play().then(() => this.bkgrAudio.muted = muted)
    }

    get backgroundAudioHasFinised(): boolean {
        return this._bkgrAudioHasFinished
    }
}

/**
 * Route a media element into the audio graph.
 *
 * Returns the source node rather than the destination that was passed in: the node is the only handle
 * that can later `disconnect()`, and without it every element routed here leaked its node — still wired
 * to both the stream destination and `audioContext.destination` — for the lifetime of the page. No caller
 * ever used the old return value.
 *
 * Note `createMediaElementSource` binds to the element permanently and throws `InvalidStateError` if it
 * runs on the same element twice, so callers that reuse elements must route each one exactly once.
 */
const addMediaElementToAudioStreamDestination = (audioContext: AudioContext, audioStreamDestination: MediaStreamAudioDestinationNode, element: HTMLVideoElement | HTMLAudioElement): MediaElementAudioSourceNode => {
    const audioSourceNode = audioContext.createMediaElementSource(element);
    audioSourceNode.connect(audioStreamDestination);
    audioSourceNode.connect(audioContext.destination);
    return audioSourceNode
}



export { SceneAudioManager, addMediaElementToAudioStreamDestination };